import ControlPanelController from "./control_panel_controller"
import Sortable from "sortablejs"

const DRAG_SVG = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"/></svg>'
const CHEVRON_SVG = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-3.5 h-3.5 transition-transform"><path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"/></svg>'
// MDI folder (filled) — plain folder
const FOLDER_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5"><path d="M10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6H12L10,4Z"/></svg>'
// MDI folder-account (filled) — shared folder with person
const FOLDER_SHARED_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5"><path d="M19,17H11V16C11,14.67 13.67,14 15,14C16.33,14 19,14.67 19,16M15,9A2,2 0 0,1 17,11A2,2 0 0,1 15,13A2,2 0 0,1 13,11C13,9.89 13.9,9 15,9M20,6H12L10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6Z"/></svg>'

export default class extends ControlPanelController {
  static targets = [
    "treeContainer", "emptyState",
    "editBtn", "deleteBtn", "expandBtn",
    "addFolderModal", "addFolderName", "addPrivacy",
    "editFolderModal", "editFolderName", "editPrivacy",
    "deleteModal", "deleteMessage"
  ]

  static values = {
    updateTemplateUrl: String,
    toggleReUrl: String,
    treeData: String
  }

  // ── Lifecycle ───────────────────────────────────────────────────────

  connect() {
    super.connect()
    this._sortableInstances = []
    this._selectedNodeId = null
    this._expandedNodes = new Set()
    this._allExpanded = false

    try {
      this._treeData = this.treeDataValue ? JSON.parse(this.treeDataValue) : []
      if (!Array.isArray(this._treeData)) this._treeData = []
    } catch {
      this._treeData = []
    }

    // Normalize legacy privacy format to privacy_settings
    const hadLegacyNodes = this._normalizeLegacyNodes(this._treeData)

    // Auto-expand all on first load
    this._collectAllIds(this._treeData).forEach(id => this._expandedNodes.add(id))
    if (this._expandedNodes.size > 0) this._allExpanded = true

    this._renderTree()

    if (hadLegacyNodes) this._autoSave()
  }

  disconnect() {
    this._destroySortables()
  }

  // ── Tree Rendering ──────────────────────────────────────────────────

  _renderTree() {
    const container = this.treeContainerTarget
    container.innerHTML = ""

    if (this._treeData.length === 0) {
      container.classList.add("hidden")
      this.emptyStateTarget.classList.remove("hidden")
    } else {
      container.classList.remove("hidden")
      this.emptyStateTarget.classList.add("hidden")
      this._renderNodes(this._treeData, container, 0)
    }

    this._initAllSortables()
    this._updateToolbarState()
  }

  _renderNodes(nodes, parentUl, depth) {
    nodes.forEach(node => {
      const li = document.createElement("li")
      li.dataset.nodeId = node.id
      li.className = "folder-row"

      const hasChildren = node.children && node.children.length > 0
      const isExpanded = this._expandedNodes.has(node.id)
      const isShared = node.privacy_settings && node.privacy_settings.length > 0
      const isSelected = this._selectedNodeId === node.id

      // Row container
      const row = document.createElement("div")
      row.className = `flex items-center gap-1.5 px-2 py-1 cursor-pointer select-none group ${
        isSelected ? "bg-blue-50 border-l-2 border-brand" : "border-l-2 border-transparent hover:bg-gray-50"
      }`
      row.style.paddingLeft = `${depth * 24 + 8}px`
      row.dataset.action = "click->cpanel-documents#selectNode"
      row.dataset.nodeId = node.id

      // Drag handle
      const handle = document.createElement("div")
      handle.className = "drag-handle cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 shrink-0"
      handle.innerHTML = DRAG_SVG

      // Chevron (expand/collapse)
      const chevron = document.createElement("div")
      chevron.className = `shrink-0 w-4 h-4 flex items-center justify-center ${hasChildren ? "text-gray-400 hover:text-gray-600" : ""}`
      if (hasChildren) {
        chevron.innerHTML = CHEVRON_SVG
        if (isExpanded) chevron.querySelector("svg").style.transform = "rotate(90deg)"
        chevron.dataset.action = "click->cpanel-documents#toggleNode"
        chevron.dataset.nodeId = node.id
        chevron.style.cursor = "pointer"
      }

      // Folder icon
      const folderIcon = document.createElement("div")
      folderIcon.className = "shrink-0 text-brand"
      folderIcon.innerHTML = isShared ? FOLDER_SHARED_SVG : FOLDER_SVG

      // Name
      const name = document.createElement("span")
      name.className = "text-sm text-gray-800 truncate flex-1"
      name.textContent = node.name

      // Shared badge
      const badge = document.createElement("span")
      if (isShared) {
        badge.className = "inline-flex items-center gap-1 shrink-0"
        badge.innerHTML = `<span class="px-1.5 py-0.5 rounded-full text-[13px] leading-none bg-brand/15 text-brand font-medium">Shared</span><span class="px-1.5 py-0.5 rounded-full text-[13px] leading-none bg-gray-100 text-gray-400 font-medium">${node.privacy_settings.length}/4</span>`
      }

      row.appendChild(handle)
      row.appendChild(chevron)
      row.appendChild(folderIcon)
      row.appendChild(name)
      if (isShared) row.appendChild(badge)
      li.appendChild(row)

      // Children
      const childUl = document.createElement("ul")
      childUl.className = `sortable-list ${hasChildren && isExpanded ? "" : "hidden"}`
      childUl.dataset.parentId = node.id
      if (hasChildren) {
        this._renderNodes(node.children, childUl, depth + 1)
      }
      li.appendChild(childUl)

      parentUl.appendChild(li)
    })
  }

  // ── SortableJS ──────────────────────────────────────────────────────

  _initAllSortables() {
    this._destroySortables()

    // Root list
    const rootUl = this.treeContainerTarget
    this._createSortable(rootUl)

    // All nested ULs
    rootUl.querySelectorAll("ul.sortable-list").forEach(ul => {
      this._createSortable(ul)
    })
  }

  _createSortable(ul) {
    const instance = Sortable.create(ul, {
      group: "folder-tree",
      animation: 150,
      handle: ".drag-handle",
      fallbackOnBody: true,
      swapThreshold: 0.65,
      emptyInsertThreshold: 8,
      ghostClass: "opacity-30",
      onEnd: () => {
        this._treeData = this._rebuildTreeFromDom()
        this._autoSave()
      }
    })
    this._sortableInstances.push(instance)
  }

  _destroySortables() {
    (this._sortableInstances || []).forEach(s => s.destroy())
    this._sortableInstances = []
  }

  _rebuildTreeFromDom() {
    return this._readChildrenFromUl(this.treeContainerTarget)
  }

  _readChildrenFromUl(ul) {
    const nodes = []
    ul.querySelectorAll(":scope > li.folder-row").forEach(li => {
      const id = parseInt(li.dataset.nodeId)
      const original = this._findNodeById(id, this._treeData)
      if (!original) return

      const node = {
        name: original.name,
        id: original.id,
        company_role: original.company_role || "owner",
        privacy_settings: original.privacy_settings || []
      }

      const childUl = li.querySelector(":scope > ul.sortable-list")
      if (childUl) {
        const children = this._readChildrenFromUl(childUl)
        if (children.length > 0) node.children = children
      }

      nodes.push(node)
    })
    return nodes
  }

  // ── Selection ───────────────────────────────────────────────────────

  selectNode(event) {
    event.stopPropagation()
    const nodeId = parseInt(event.currentTarget.dataset.nodeId)

    if (this._selectedNodeId === nodeId) {
      this._selectedNodeId = null
    } else {
      this._selectedNodeId = nodeId
    }

    this._renderTree()
  }

  // ── Expand / Collapse ──────────────────────────────────────────────

  toggleNode(event) {
    event.stopPropagation()
    const nodeId = parseInt(event.currentTarget.dataset.nodeId)

    if (this._expandedNodes.has(nodeId)) {
      this._expandedNodes.delete(nodeId)
    } else {
      this._expandedNodes.add(nodeId)
    }

    this._renderTree()
  }

  toggleExpandAll() {
    if (this._allExpanded) {
      this._expandedNodes.clear()
      this._allExpanded = false
    } else {
      this._collectAllIds(this._treeData).forEach(id => this._expandedNodes.add(id))
      this._allExpanded = true
    }

    this._renderTree()
  }

  // ── Add Folder ──────────────────────────────────────────────────────

  openAddFolderModal() {
    this.addFolderNameTarget.value = ""
    this.addPrivacyTargets.forEach(cb => cb.checked = false)
    this.addFolderModalTarget.classList.remove("hidden")
    this._lockScroll()
    setTimeout(() => this.addFolderNameTarget.focus(), 100)
  }

  closeAddFolderModal() {
    this.addFolderModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  submitAddFolder() {
    const name = this.addFolderNameTarget.value.trim()
    if (!name) {
      this.addFolderNameTarget.focus()
      return
    }

    const privacySettings = []
    this.addPrivacyTargets.forEach(cb => {
      if (cb.checked) privacySettings.push(cb.value)
    })

    const newNode = {
      name,
      id: Date.now() + Math.round(Math.random() * 1000),
      company_role: "owner",
      privacy_settings: privacySettings
    }

    if (this._selectedNodeId) {
      const parent = this._findNodeById(this._selectedNodeId, this._treeData)
      if (parent) {
        if (!parent.children) parent.children = []
        parent.children.push(newNode)
        this._expandedNodes.add(this._selectedNodeId)
      } else {
        this._treeData.push(newNode)
      }
    } else {
      this._treeData.push(newNode)
    }

    this.closeAddFolderModal()
    this._renderTree()
    this._autoSave()
    this._showToast("Folder added")
  }

  // ── Edit Folder ─────────────────────────────────────────────────────

  openEditFolderModal() {
    if (!this._selectedNodeId) return
    const node = this._findNodeById(this._selectedNodeId, this._treeData)
    if (!node) return

    this.editFolderNameTarget.value = node.name
    const ps = node.privacy_settings || []
    this.editPrivacyTargets.forEach(cb => {
      cb.checked = ps.includes(cb.value)
    })

    this.editFolderModalTarget.classList.remove("hidden")
    this._lockScroll()
    setTimeout(() => this.editFolderNameTarget.focus(), 100)
  }

  closeEditFolderModal() {
    this.editFolderModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  submitEditFolder() {
    const name = this.editFolderNameTarget.value.trim()
    if (!name) {
      this.editFolderNameTarget.focus()
      return
    }

    const node = this._findNodeById(this._selectedNodeId, this._treeData)
    if (!node) return

    node.name = name
    node.privacy_settings = []
    this.editPrivacyTargets.forEach(cb => {
      if (cb.checked) node.privacy_settings.push(cb.value)
    })

    this.closeEditFolderModal()
    this._renderTree()
    this._autoSave()
    this._showToast("Folder updated")
  }

  // ── Delete Folder ───────────────────────────────────────────────────

  openDeleteModal() {
    if (!this._selectedNodeId) return
    const node = this._findNodeById(this._selectedNodeId, this._treeData)
    if (!node) return

    const hasChildren = node.children && node.children.length > 0
    this.deleteMessageTarget.textContent = hasChildren
      ? `Are you sure you want to delete "${node.name}"? This will also remove all ${node.children.length} sub-folder(s).`
      : `Are you sure you want to delete "${node.name}"?`

    this.deleteModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  closeDeleteModal() {
    this.deleteModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  confirmDelete() {
    this._removeNodeById(this._selectedNodeId, this._treeData)
    this._selectedNodeId = null
    this.closeDeleteModal()
    this._renderTree()
    this._autoSave()
    this._showToast("Folder deleted")
  }

  // ── RE Stage Toggle ─────────────────────────────────────────────────

  async toggleReFolders(event) {
    if (!this.hasToggleReUrlValue) return
    const checkbox = event.currentTarget
    const checked = checkbox.checked
    try {
      const response = await fetch(this.toggleReUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ enabled: checked })
      })
      if (response.ok) {
        this._showToast("Updated successfully")
      } else {
        checkbox.checked = !checked
        this._showToast("Error updating", "error")
      }
    } catch {
      checkbox.checked = !checked
      this._showToast("Network error", "error")
    }
  }

  // ── Auto-save ───────────────────────────────────────────────────────

  _autoSave() {
    clearTimeout(this._saveTimeout)
    this._saveTimeout = setTimeout(() => this._save(), 500)
  }

  async _save() {
    if (!this.hasUpdateTemplateUrlValue) return
    try {
      const response = await fetch(this.updateTemplateUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ data: JSON.stringify(this._serializeTree(this._treeData)) })
      })
      if (!response.ok) {
        this._showToast("Error saving template", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  _serializeTree(nodes) {
    return nodes.map(node => {
      const out = {
        name: node.name,
        id: node.id,
        company_role: node.company_role || "owner",
        privacy_settings: node.privacy_settings || []
      }
      if (node.children && node.children.length > 0) {
        out.children = this._serializeTree(node.children)
      }
      return out
    })
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  _findNodeById(id, nodes) {
    for (const node of nodes) {
      if (node.id === id) return node
      if (node.children) {
        const found = this._findNodeById(id, node.children)
        if (found) return found
      }
    }
    return null
  }

  _removeNodeById(id, nodes) {
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].id === id) {
        nodes.splice(i, 1)
        return true
      }
      if (nodes[i].children) {
        if (this._removeNodeById(id, nodes[i].children)) return true
      }
    }
    return false
  }

  // Convert legacy privacy: "0"/"1"/"2"/etc. to privacy_settings array
  // Returns true if any nodes were normalized
  _normalizeLegacyNodes(nodes) {
    const LEGACY_MAP = {
      "0": [],
      "1": ["consultants"],
      "2": ["contractors"],
      "3": ["consultants", "contractors"],
      "4": ["consultants", "other_vendors"],
      "5": ["consultants", "contractors", "other_vendors"]
    }
    let normalized = false
    nodes.forEach(node => {
      if (!node.privacy_settings && node.privacy !== undefined) {
        node.privacy_settings = LEGACY_MAP[String(node.privacy)] || []
        delete node.privacy
        normalized = true
      }
      if (!node.privacy_settings) node.privacy_settings = []
      if (node.children && this._normalizeLegacyNodes(node.children)) normalized = true
    })
    return normalized
  }

  _collectAllIds(nodes) {
    const ids = []
    nodes.forEach(node => {
      if (node.children && node.children.length > 0) {
        ids.push(node.id)
        ids.push(...this._collectAllIds(node.children))
      }
    })
    return ids
  }

  _updateToolbarState() {
    const hasSelection = this._selectedNodeId !== null
    if (this.hasEditBtnTarget) this.editBtnTarget.disabled = !hasSelection
    if (this.hasDeleteBtnTarget) this.deleteBtnTarget.disabled = !hasSelection

    // Update expand/collapse button text
    if (this.hasExpandBtnTarget) {
      const svgEl = this.expandBtnTarget.querySelector("svg")
      const svgHtml = svgEl ? svgEl.outerHTML : ""
      const text = this._allExpanded ? "Collapse All" : "Expand All"
      this.expandBtnTarget.innerHTML = `${svgHtml}\n          ${text}`
    }
  }
}
